/**
 * 电子宠物 · 定时提醒 API。
 * P0:表单建提醒 + inbox 轮询,到点念静态文案。
 * P1:自然语言 parse → 服务端 LLM 抽排程并落库。
 */
import { apiRequest } from './client'

export type ReminderKind = 'at' | 'every' | 'cron'

export interface PetReminder {
  id: number
  title: string
  kind: ReminderKind
  cron_expr: string | null
  every_ms: number | null
  at_ts: string | null
  tz: string
  next_run_at: string | null
  last_run_at: string | null
  enabled: boolean
  created_at: string
}

export interface ReminderInboxEvent {
  id: number
  reminder_id: number
  title: string
  fire_at: string
}

export interface CreateReminderBody {
  title: string
  kind: ReminderKind
  cron_expr?: string
  every_ms?: number
  at_ts?: string
  tz?: string
}

export interface ParseReminderResult {
  created: boolean
  /** true = 不像设提醒,前端应改走闲聊 */
  not_reminder?: boolean
  reminder?: PetReminder
  reply?: string
}

/** 表单 → API:一次性 / 每天 / 每周 / 每隔 N 小时。 */
export function buildReminderBody(form: {
  title: string
  schedule: 'once' | 'daily' | 'weekly' | 'every'
  /** datetime-local 值,如 2026-07-06T10:00 */
  onceLocal?: string
  /** HH:mm */
  time?: string
  /** 0=周日 … 6=周六 */
  weekday?: number
  everyHours?: number
}): CreateReminderBody {
  const title = form.title.trim()
  const tz = 'Asia/Shanghai'
  if (form.schedule === 'once') {
    if (!form.onceLocal) throw new Error('请选择时间')
    const at = new Date(form.onceLocal)
    if (Number.isNaN(at.getTime())) throw new Error('时间无效')
    return { title, kind: 'at', at_ts: at.toISOString(), tz }
  }
  const [hh, mm] = (form.time ?? '09:00').split(':').map((s) => parseInt(s, 10))
  if (form.schedule === 'daily') {
    return { title, kind: 'cron', cron_expr: `${mm} ${hh} * * *`, tz }
  }
  if (form.schedule === 'weekly') {
    const dow = form.weekday ?? 1
    return { title, kind: 'cron', cron_expr: `${mm} ${hh} * * ${dow}`, tz }
  }
  const hours = form.everyHours ?? 2
  if (hours < 1) throw new Error('间隔至少 1 小时')
  return { title, kind: 'every', every_ms: hours * 3_600_000, tz }
}

export function fetchReminders(): Promise<{ items: PetReminder[] }> {
  return apiRequest<{ items: PetReminder[] }>('/pet/reminders')
}

export function createReminder(body: CreateReminderBody): Promise<PetReminder> {
  return apiRequest<PetReminder>('/pet/reminders', { method: 'POST', jsonBody: body })
}

/** P1:自然语言建提醒(服务端 LLM 解析)。not_reminder 时前端改走闲聊。 */
export function parseReminderIntent(message: string): Promise<ParseReminderResult> {
  return apiRequest<ParseReminderResult>('/pet/reminders/parse', {
    method: 'POST',
    jsonBody: { message }
  })
}

export function patchReminder(
  id: number,
  body: Partial<CreateReminderBody> & { enabled?: boolean }
): Promise<PetReminder> {
  return apiRequest<PetReminder>(`/pet/reminders/${id}`, {
    method: 'PATCH',
    jsonBody: body
  })
}

export function deleteReminder(id: number): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/pet/reminders/${id}`, { method: 'DELETE' })
}

/** 拉待发提醒;服务端原子标记 delivered。 */
export function fetchReminderInbox(): Promise<{ items: ReminderInboxEvent[] }> {
  return apiRequest<{ items: ReminderInboxEvent[] }>('/pet/reminders/inbox')
}

/** 人类可读排程摘要。 */
export function formatReminderSchedule(r: PetReminder): string {
  if (!r.enabled) return '已暂停'
  if (r.kind === 'at' && r.at_ts) {
    return `一次 · ${r.at_ts.slice(0, 16).replace('T', ' ')}`
  }
  if (r.kind === 'every' && r.every_ms) {
    const h = r.every_ms / 3_600_000
    return h >= 1 ? `每 ${h} 小时` : `每 ${r.every_ms / 60_000} 分钟`
  }
  if (r.kind === 'cron' && r.cron_expr) {
    const p = r.cron_expr.split(/\s+/)
    if (p.length === 5 && p[2] === '*' && p[3] === '*' && p[4] === '*') {
      return `每天 ${p[1].padStart(2, '0')}:${p[0].padStart(2, '0')}`
    }
    if (p.length === 5 && p[2] === '*' && p[3] === '*' && p[4] !== '*') {
      const days = ['日', '一', '二', '三', '四', '五', '六']
      return `每周${days[Number(p[4])] ?? p[4]} ${p[1].padStart(2, '0')}:${p[0].padStart(2, '0')}`
    }
    return r.cron_expr
  }
  return '—'
}
